import type { BookResponse } from '../lib/api/types';

type BookSuccess = Extract<BookResponse, { ok: true }>;

interface Props {
  booking: BookSuccess;
  prospectEmail: string;
}

const TZ = 'America/New_York';

/**
 * Public WordPress uploads base for advisor headshots. Mirrors the constant
 * used in api/_lib/email-templates.ts so the post-booking reveal photo
 * matches the photo used in confirmation/reminder emails.
 */
const HEADSHOT_BASE_URL = 'https://annuityassociation.com/wp-content/uploads/2026/03/';

function formatMeetingTime(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}

/**
 * Strip everything that isn't a digit or leading + so the tel: href is
 * dial-friendly across iOS, Android, and desktop softphones.
 */
function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? digits : `+1${digits}`;
}

export function Booked({ booking, prospectEmail }: Props) {
  const { advisor } = booking;
  const fullName = `${advisor.firstName} ${advisor.lastName}`.trim();
  const headshotUrl = advisor.headshotFilename
    ? `${HEADSHOT_BASE_URL}${advisor.headshotFilename}`
    : null;

  return (
    <div className="booked-state">
      <div className="booked-badge">
        <span className="booked-check">✓</span>
        <span>Booked</span>
      </div>

      <h2 className="booked-headline">
        You're set with {advisor.firstName}
      </h2>

      <div className="booked-meeting">
        <div className="booked-when">
          {formatMeetingTime(booking.meetingStartUtc)}
        </div>
        <div className="booked-format">60-minute Zoom call</div>
      </div>

      {booking.zoomJoinUrl && (
        <a
          href={booking.zoomJoinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="booked-zoom-btn"
        >
          Join Zoom Meeting
        </a>
      )}

      {/* Advisor reveal — headshot, name, title, bio, direct phone */}
      <section className="booked-advisor-card" aria-label={`About ${fullName}`}>
        <div className="booked-advisor-header">
          {headshotUrl && (
            <img
              src={headshotUrl}
              alt={fullName}
              width={120}
              height={120}
              className="booked-advisor-headshot"
            />
          )}
          <div className="booked-advisor-meta">
            <h3 className="booked-advisor-name">{fullName}</h3>
            {advisor.title && (
              <div className="booked-advisor-title">{advisor.title}</div>
            )}
            <div className="booked-advisor-org">Annuity Association</div>
            {advisor.phone && (
              <a
                className="booked-advisor-phone"
                href={`tel:${telHref(advisor.phone)}`}
              >
                {advisor.phone}
              </a>
            )}
          </div>
        </div>

        {advisor.bio && (
          <div className="booked-advisor-bio">
            <h4 className="booked-advisor-bio-label">A note from {advisor.firstName}</h4>
            <p>{advisor.bio}</p>
          </div>
        )}
      </section>

      <div className="booked-next">
        <h3>What happens next</h3>
        <p>
          A confirmation email is on its way to{' '}
          <strong>{prospectEmail}</strong> with the meeting link and a
          calendar invite. {advisor.firstName} will join you on Zoom a few
          minutes early.
        </p>
        <p className="booked-followup">
          Need to reschedule or have a question? Reach out to{' '}
          <a href="mailto:scheduler@annuityassociation.com">
            scheduler@annuityassociation.com
          </a>{' '}
          with your reference number.
        </p>
      </div>

      <div className="booked-ref">
        Reference: <code>{booking.bookingId.slice(0, 8).toUpperCase()}</code>
      </div>
    </div>
  );
}
